import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
} from '@mui/material'
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material'
import { invitationApi, InvitationValidation } from '../../services/userApi'
import { googleSignInButtonSx } from '../../components/auth/authButtonStyles'
import { GoogleIcon } from '../../components/SystemIcons'
import { login } from '../../services/auth'
import poolwerxLogo from '../../assets/poolwerx-logo.png'
import DevEnvironmentBanner from '../../components/DevEnvironmentBanner'

const InvitationLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [invitation, setInvitation] = useState<InvitationValidation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accepting, setAccepting] = useState(false)

  useEffect(() => {
    if (token) {
      validateInvitation(token)
    } else {
      setError('Invalid invitation link')
      setLoading(false)
    }
  }, [token])

  const validateInvitation = async (inviteToken: string) => {
    try {
      setLoading(true)
      setError(null)
      const response = await invitationApi.validateInvitation(inviteToken)
      setInvitation(response.data)
    } catch (err: any) {
      console.error('Failed to validate invitation:', err)
      setError(err.response?.data?.detail || 'Invalid or expired invitation')
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptInvitation = async () => {
    if (!token) return
    
    try {
      setAccepting(true)
      // Start the OAuth login process with the invitation token
      // The backend will handle linking the user to the FP after OAuth completes
      login(`/invitation/${token}/accept`)
    } catch (err: any) {
      console.error('Failed to accept invitation:', err)
      setError(err.response?.data?.detail || 'Failed to accept invitation')
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}
      >
        <CircularProgress size={60} sx={{ color: '#007f98' }} />
        <DevEnvironmentBanner />
      </Box>
    )
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5',
        py: 8
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={4} alignItems="center">
          {/* Logo */}
          <Box sx={{ textAlign: 'center' }}>
            <img 
              src={poolwerxLogo} 
              alt="Poolwerx" 
              style={{ height: '80px', marginBottom: '16px' }}
            />
            <Typography 
              variant="h4" 
              fontWeight={700}
              sx={{ 
                color: '#007f98',
                textAlign: 'center',
                mb: 1
              }}
            >
              Inspector Tool
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ textAlign: 'center' }}
            >
              Inspector Access Invitation
            </Typography>
          </Box>

          {/* Invitation Card */}
          <Card sx={{ width: '100%', boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              {error ? (
                <Stack spacing={3} alignItems="center">
                  <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
                    <ErrorIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h5" fontWeight={600} color="error" textAlign="center">
                    Invalid Invitation
                  </Typography>
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {error}
                  </Alert>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    This invitation link may have expired or been revoked. 
                    Please contact your Poolwerx administrator for a new invitation.
                  </Typography>
                </Stack>
              ) : invitation && invitation.valid ? (
                <Stack spacing={3} alignItems="center">
                  <Avatar sx={{ bgcolor: '#007f98', width: 64, height: 64 }}>
                    <BusinessIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                      You're Invited!
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      You've been invited to access the Poolwerx Inspector for:
                    </Typography>
                  </Box>

                  <Card sx={{ bgcolor: '#f8f9fa', width: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight={600} sx={{ color: '#007f98' }}>
                        {invitation.fp_code}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {invitation.franchise_name}
                      </Typography>
                      <Chip 
                        label={`Expires: ${new Date(invitation.expires_at).toLocaleDateString()}`}
                        size="small"
                        sx={{ bgcolor: 'rgba(0, 127, 152, 0.1)', color: '#007f98' }}
                      />
                    </CardContent>
                  </Card>

                  <Stack spacing={2} sx={{ width: '100%' }}>
                    <Button
                      variant="outlined"
                      startIcon={accepting ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
                      onClick={handleAcceptInvitation}
                      disabled={accepting}
                      sx={googleSignInButtonSx}
                      fullWidth
                    >
                      {accepting ? 'Setting up access...' : 'Sign in with Google'}
                    </Button>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      You'll be signed in with your Google account and
                      redirected to the Inspector dashboard.
                    </Typography>
                  </Stack>

                  <Alert severity="info" sx={{ width: '100%' }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> You must sign in with the email address the invitation was sent to.
                    </Typography>
                  </Alert>
                </Stack>
              ) : (
                <Stack spacing={3} alignItems="center">
                  <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
                    <ErrorIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h5" fontWeight={600} color="error" textAlign="center">
                    Invitation Not Valid
                  </Typography>
                  <Typography variant="body1" color="text.secondary" textAlign="center">
                    This invitation is no longer valid or has been used.
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Footer */}
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Need help? Contact your Poolwerx administrator or 
            <br />visit our support portal for assistance.
          </Typography>
        </Stack>
      </Container>
      <DevEnvironmentBanner />
    </Box>
  )
}

export default InvitationLanding